import { getConfig } from './config';
import { ProblemSchema, type Problem } from './schemas';
import type { z } from 'zod';

/**
 * A typed error the UI can branch on.
 *
 * It carries the correlation id so an error state can show it — which is the difference between
 * "something went wrong" and a support ticket someone can actually trace.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly correlationId: string,
    readonly fieldErrors: Record<string, string[]> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The session is gone. The auth boundary redirects rather than showing an error. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Authenticated but the role is wrong. Showing a sign-in prompt here would be a dead end. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** A state conflict — editing a sent campaign, scheduling an unapproved one. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

/**
 * How the client obtains a bearer token, injected rather than imported.
 *
 * Injected because importing the Keycloak module here would make every test that touches the API
 * client need a Keycloak instance — and the tests that matter most are exactly the ones asserting
 * that a malformed response is rejected, which has nothing to do with auth.
 */
type TokenProvider = () => Promise<string | undefined>;

// Not `async () => undefined`: an async function with no await is flagged, and the default is a
// resolved promise rather than a suspended one anyway.
let getToken: TokenProvider = () => Promise.resolve(undefined);

export function setTokenProvider(provider: TokenProvider): void {
  getToken = provider;
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

/**
 * The one place an HTTP request is made.
 *
 * Every response is parsed through a Zod schema, so callers receive validated types and nothing
 * downstream needs to defend against a shape it did not expect.
 */
export async function request<TOutput>(
  path: string,
  // `z.ZodType<TOutput>` rather than `T extends z.ZodTypeAny`: with the latter, `safeParse().data`
  // widens to `any` and every guarantee this function exists to provide is silently lost at the
  // return statement. Parameterising on the OUTPUT type keeps it checked.
  schema: z.ZodType<TOutput>,
  options: RequestOptions = {},
): Promise<TOutput> {
  const response = await send(path, options);
  const payload: unknown = await readJson(response);

  if (!response.ok) throw toApiError(response.status, payload);

  const parsed = schema.safeParse(payload);

  if (!parsed.success) {
    // A contract break, not a server error — the request succeeded and returned something we do
    // not understand. Surfaced loudly rather than coerced, because silently accepting a partial
    // shape is how a missing field becomes a blank screen three components away.
    throw new ApiError(
      response.status,
      'contract_mismatch',
      `The API returned an unexpected shape for ${path}: ${parsed.error.issues
        .map((issue) => `${issue.path.join('.')} ${issue.message}`)
        .join('; ')}`,
      response.headers.get('X-Correlation-Id') ?? 'unknown',
    );
  }

  return parsed.data;
}

/** For the few endpoints with no response body. */
export async function requestVoid(path: string, options: RequestOptions = {}): Promise<void> {
  const response = await send(path, options);

  if (!response.ok) throw toApiError(response.status, await readJson(response));
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { apiBaseUrl } = getConfig();
  const token = await getToken();

  const headers: Record<string, string> = { Accept: 'application/json' };

  // FormData is passed through untouched, and Content-Type is deliberately NOT set for it: the
  // browser has to generate the multipart boundary itself, and setting the header by hand produces a
  // request the server cannot parse. JSON-stringifying it would send `{}` — silently, with a 200.
  const isFormData = options.body instanceof FormData;

  if (options.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  if (token !== undefined) headers.Authorization = `Bearer ${token}`;

  const body = isFormData ? (options.body as FormData) : JSON.stringify(options.body);

  // Spread rather than assigning undefined: with exactOptionalPropertyTypes, `body: undefined`
  // is a different type from an absent `body`, and RequestInit does not accept the former.
  return fetch(`${apiBaseUrl}${path}`, {
    method: options.method ?? 'GET',
    headers,
    ...(options.body === undefined ? {} : { body }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
    // Same-origin in both environments, so no credentials mode is needed and CORS never
    // enters the picture.
  });
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();

  if (text.length === 0) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A non-JSON body from an endpoint that promises JSON — usually a proxy or gateway error
    // page. Returning the text lets the error message say something useful.
    return { error: 'non_json_response', message: text.slice(0, 300), correlationId: 'unknown' };
  }
}

function toApiError(status: number, payload: unknown): ApiError {
  const parsed = ProblemSchema.safeParse(payload);

  if (parsed.success) {
    const problem: Problem = parsed.data;

    return new ApiError(
      status,
      problem.error,
      problem.message,
      problem.correlationId,
      problem.errors ?? null,
    );
  }

  return new ApiError(
    status,
    'unexpected_error',
    `The request failed with status ${status}.`,
    'unknown',
  );
}
